import React, { useMemo, useState } from "react";
import { useShow, useCustomMutation, useGetIdentity, useList, useCan } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import {
  Card,
  Descriptions,
  Row,
  Col,
  Table,
  Tag,
  Space,
  Button,
  Modal,
  InputNumber,
  Input,
  message,
  Select,
} from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  PURCHASE_REQUEST_ONE_QUERY,
  VALIDATE_PURCHASE_REQUEST_BUDGET_MUTATION,
  APPROVE_PURCHASE_REQUEST_MUTATION,
  REJECT_PURCHASE_REQUEST_MUTATION,
  ASSIGN_PURCHASE_REQUEST_MUTATION,
} from "./queries";
import { ApprovalDetailsTable } from "./components/ApprovalDetailsTable";
import { SubmitButton } from "./components/SubmitButton";
import { FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import { UserInfo } from "../../components/UserInfo";
import { USER_LIST_QUERY } from "../users/queries";

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const color: Record<string, string> = {
    DRAFT: "default",
    SUBMITTED: "processing",
    VALIDATED: "purple",
    PARTIALLY_APPROVED: "warning",
    APPROVED: "success",
    REJECTED: "error",
  };
  return <Tag color={color[status] || "default"}>{status}</Tag>;
};

// Helper function to get appropriate icon based on mimeType
const getMimeTypeIcon = (mimeType: string) => {
  if (mimeType.includes("pdf")) {
    return <FilePdfOutlined />;
  } else if (mimeType.includes("image")) {
    return <FileImageOutlined />;
  } else if (mimeType.includes("text")) {
    return <FileTextOutlined />;
  }
  return <FileOutlined />;
};

// Helper function to format file size
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const ShowPurchaseRequest: React.FC = () => {
  const [isValidateOpen, setIsValidateOpen] = useState(false);
  const [validateAmount, setValidateAmount] = useState<number | undefined>(undefined);
  const [validateRemarks, setValidateRemarks] = useState<string>("");

  const [isDecisionOpen, setIsDecisionOpen] = useState<"approve" | "reject" | null>(null);
  const [decisionRemarks, setDecisionRemarks] = useState<string>("");

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<string | undefined>(undefined);

  const { query } = useShow({
    resource: "purchaseRequests",
    meta: {
      gqlQuery: PURCHASE_REQUEST_ONE_QUERY,
    },
  });

  const { data: identity } = useGetIdentity();
  const currentUserRole = (identity as any)?.organizationalRole;

  const { data: usersData, isLoading: usersLoading } = useList<any>({
    resource: "users",
    meta: { gqlQuery: USER_LIST_QUERY },
    pagination: { mode: "off" },
  });
  const users = (usersData?.data as any) ?? [];
  const activeUsers = users.filter((u: any) => u.isActive);

  const { data, isLoading } = query;
  const record = data?.data;
  const navigate = useNavigate();

  const handleSubmitSuccess = () => {
    // Refresh the data after successful submission
    query.refetch();
  };

  const { mutate: validateMutate, isLoading: validating } = useCustomMutation();
  const { mutate: approveMutate, isLoading: approving } = useCustomMutation();
  const { mutate: rejectMutate, isLoading: rejecting } = useCustomMutation();
  const { mutate: assignMutate, isLoading: assigning } = useCustomMutation();

  const totalFromItems = useMemo(() => {
    if (!record?.items?.length) return 0;
    return record.items.reduce((s: number, it: any) => s + Number(it.totalPrice || 0), 0);
  }, [record?.items]);

  const currentPendingStep = useMemo(() => {
    const steps = (record?.approvals || []).slice().sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    return steps.find((s: any) => s.stepStatus === "PENDING");
  }, [record?.approvals]);

  const handleValidateBudget = async () => {
    if (!record?.id) return;
    validateMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            requestId: record.id,
            totalAmount: Number(validateAmount ?? totalFromItems),
            remarks: validateRemarks || undefined,
          },
        },
        meta: { gqlMutation: VALIDATE_PURCHASE_REQUEST_BUDGET_MUTATION },
      },
      {
        onSuccess: () => {
          message.success("Budget validated");
          setIsValidateOpen(false);
          setValidateRemarks("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Failed to validate budget");
        },
      },
    );
  };

  const handleApproveReject = async () => {
    if (!record?.id || !currentPendingStep?.roleSlug || !isDecisionOpen) return;
    const meta = isDecisionOpen === "approve" ? { gqlMutation: APPROVE_PURCHASE_REQUEST_MUTATION } : { gqlMutation: REJECT_PURCHASE_REQUEST_MUTATION };
    const mutate = isDecisionOpen === "approve" ? approveMutate : rejectMutate;
    mutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            requestId: record.id,
            roleSlug: currentPendingStep.roleSlug,
            remarks: decisionRemarks || undefined,
          },
        },
        meta,
      },
      {
        onSuccess: () => {
          message.success(isDecisionOpen === "approve" ? "Approved" : "Rejected");
          setIsDecisionOpen(null);
          setDecisionRemarks("");
          query.refetch();
        },
        onError: (e) => {
          console.error(e);
          message.error("Action failed");
        },
      },
    );
  };

  const handleAssign = async () => {
    if (!record?.id) return;
    assignMutate(
      {
        url: "",
        method: "post",
        values: {
          input: {
            requestId: record.id,
            assignedTo: selectedAssignee || null,
          },
        },
        meta: { gqlMutation: ASSIGN_PURCHASE_REQUEST_MUTATION },
      },
      {
        onSuccess: () => {
          message.success(selectedAssignee ? "PR assigned successfully" : "PR unassigned successfully");
          setIsAssignModalOpen(false);
          setSelectedAssignee(undefined);
          query.refetch();
        },
        onError: (e: any) => {
          console.error(e);
          message.error(e?.response?.errors?.[0]?.message || "Assignment failed");
        },
      },
    );
  };


  const itemColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Qty", dataIndex: "quantity" },
    { title: "Requested", dataIndex: "requestedQty", width: 90 },
    { title: "In Sourcing", dataIndex: "inSourcingQty", width: 100 },
    { title: "Awarded", dataIndex: "awardedQty", width: 90 },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      render: (v: number) => `ETB ${Number(v || 0).toLocaleString()}`,
    },
    { title: "Supplier", dataIndex: "supplier" },
    {
      title: "From Plan",
      render: (_: any, r: any) =>
        r?.planRef?.planId ? <Tag color="blue">{r.planRef.planId} • {r.planRef.itemId}</Tag> : <Tag>Manual</Tag>,
    },
  ];

  const { data: canAssign } = useCan({
    resource: "purchaseRequests",
    action: "assign",
  });

  const canValidate = record?.status === "SUBMITTED";
  const canDecide = record?.status === "VALIDATED" || record?.status === "PARTIALLY_APPROVED";

  // Get current fiscal year (simplified - using current year)
  const currentFiscalYear = new Date().getFullYear();

  const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL 
  };

  const filePreviewUrl = (fileId: string) => {
    return `${getApiBaseUrl()}/file-storage/preview/${fileId}`;
  };

  return (
    <Show
      isLoading={isLoading}
      title={record ? `Purchase Request • ${record.title || record.requestNo}` : "Purchase Request"}
      headerButtons={
       record && (
         <Space>
           {record.status === 'DRAFT' && (
             <SubmitButton
               requestId={record.id as string}
               request={record}
               fiscalYear={currentFiscalYear}
               buttonText="Submit"
               onSuccess={handleSubmitSuccess}
             />
           )}
           {canValidate && (
             <Button type="primary" onClick={() => { setValidateAmount(record?.totalAmount ?? totalFromItems); setIsValidateOpen(true); }}>
               Validate Budget
             </Button>
           )}
            {canDecide && (
              <>
                <Button type="primary" onClick={() => setIsDecisionOpen("approve")} loading={approving}>
                  Approve
                </Button>
                <Button danger onClick={() => setIsDecisionOpen("reject")} loading={rejecting}>
                  Reject
                </Button>
              </>
            )}
            {record.status === "APPROVED" && (
              <Button
                type="primary"
                onClick={() => navigate(`/purchase-orders/create?fromPR=${record.id}`)}
              >
                Create Purchase Order
              </Button>
            )}
            {record.status === "APPROVED" && canAssign?.can && (
              <Button
                type="primary"
                onClick={() => {
                  setSelectedAssignee(record.assignedTo || undefined);
                  setIsAssignModalOpen(true);
                }}
              >
                {record.assignedTo ? "Reassign" : "Assign To"}
              </Button>
            )}
          </Space>
        )
      }
    >
      {record && (
        <>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Descriptions column={2} layout="vertical" bordered>
                  <Descriptions.Item label="Request No">{record.requestNo}</Descriptions.Item>
                  <Descriptions.Item label="Title">{record.title || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Status"><StatusBadge status={record.status} /></Descriptions.Item>
                  <Descriptions.Item label="Description" span={2}>{record.description || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Team">{record.teamId}</Descriptions.Item>
                   <Descriptions.Item label="Requested By">
                    {record.requestedBy || record.createdBy ? (
                      <UserInfo userId={record.requestedBy || record.createdBy} />
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Assigned To">
                    {record.assignedTo ? (
                      <UserInfo userId={record.assignedTo} />
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date Requested">{record.dateRequested ? dayjs(record.dateRequested).format("YYYY-MM-DD") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Date Required">{record.dateRequired ? dayjs(record.dateRequired).format("YYYY-MM-DD") : ""}</Descriptions.Item>
                  <Descriptions.Item label="Budget Source">{record.budgetSource}</Descriptions.Item>
                  <Descriptions.Item label="Budget ID">{record.budgetId || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Total Amount">ETB {Number(record.totalAmount || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Tags">{(record.tags || []).join(", ") || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Notes" span={2}>{record.notes || "-"}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col span={24}>
              <ApprovalDetailsTable approvals={record.approvals} />
            </Col>

            <Col span={24}>
              <Card title="Items">
                <Table dataSource={record.items} columns={itemColumns as any} rowKey={(r: any) => r.id} pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>ETB {Number(totalFromItems || 0).toLocaleString()}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} colSpan={2}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Attachments">
                <Space wrap>
                  {(record.attachments || []).map((attachment: any) => {
                    // Handle both string fileId format and object format
                    const fileId = typeof attachment === 'string' ? attachment : attachment.fileId;
                    const originalName = typeof attachment === 'string' ? fileId : attachment.originalName;
                    const mimeType = typeof attachment === 'string' ? 'application/octet-stream' : attachment.mimeType;
                    const size = typeof attachment === 'string' ? 0 : attachment.size;
                    
                    return (
                      <div key={fileId} style={{ border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
                        <Space>
                          {getMimeTypeIcon(mimeType)}
                          <a href={filePreviewUrl(fileId)} target="_blank" rel="noreferrer">
                            {originalName} {size > 0 && `(${formatBytes(size)})`}
                          </a>
                        </Space>
                      </div>
                    );
                  })}
                  {!record.attachments?.length && <div>No attachments</div>}
                </Space>
                <div style={{ marginTop: 8, color: "#888" }}>
                  Upload/remove is available on Edit page.
                </div>
              </Card>
            </Col>
          </Row>

          <Modal
            title="Validate Budget"
            open={isValidateOpen}
            onOk={handleValidateBudget}
            onCancel={() => setIsValidateOpen(false)}
            confirmLoading={validating}
            okText="Validate"
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>Total Amount</div>
              <InputNumber
                value={validateAmount}
                onChange={(v) => setValidateAmount(Number(v || 0))}
                min={0}
                step={0.01}
                style={{ width: "100%" }}
              />
              <div>Remarks</div>
              <Input.TextArea rows={3} value={validateRemarks} onChange={(e) => setValidateRemarks(e.target.value)} />
            </Space>
          </Modal>

          <Modal
            title={isDecisionOpen === "approve" ? "Approve Request" : "Reject Request"}
            open={!!isDecisionOpen}
            onOk={handleApproveReject}
            onCancel={() => setIsDecisionOpen(null)}
            okText={isDecisionOpen === "approve" ? "Approve" : "Reject"}
            okButtonProps={{ danger: isDecisionOpen === "reject" }}
            confirmLoading={approving || rejecting}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>Next Step Role</div>
              <Input value={currentPendingStep?.roleSlug || "-"} disabled />
              <div>Remarks</div>
              <Input.TextArea rows={3} value={decisionRemarks} onChange={(e) => setDecisionRemarks(e.target.value)} />
            </Space>
          </Modal>

          <Modal
            title="Assign Purchase Request"
            open={isAssignModalOpen}
            onOk={handleAssign}
            onCancel={() => {
              setIsAssignModalOpen(false);
              setSelectedAssignee(undefined);
            }}
            confirmLoading={assigning}
            okText={selectedAssignee ? "Reassign" : record?.assignedTo ? "Reassign" : "Assign"}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>Current Assignment</div>
              {record?.assignedTo ? (
                <UserInfo userId={record.assignedTo} />
              ) : (
                <Tag>Unassigned</Tag>
              )}
              <div>Assign To</div>
              <Select
                placeholder="Select a user to assign"
                value={selectedAssignee}
                onChange={(val) => setSelectedAssignee(val)}
                loading={usersLoading}
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {activeUsers.map((user: any) => {
                  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
                  const label = fullName ? `${fullName} (${user.email})` : user.email;
                  return (
                    <Select.Option key={user.id} value={user.id}>
                      {label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Space>
          </Modal>
        </>
      )}
    </Show>
  );
};